import { PoemHighlight } from '@/types/highlights';
import { getDisplayLabel as getDisplayLabelFromUtils } from '@/utils/highlightLabelUtils';
import { logger } from '@/utils/logger';

/**
 * Core interfaces for the highlight processing service
 */

/**
 * Main data structure containing processed highlight information
 */
export interface ProcessedHighlightData {
  // Main processed data structure
  highlightsByLine: Record<number, LineHighlightInfo[]>;
  allHighlights: PositionedHighlight[];
  legendItems: LegendHighlightInfo[];
}

/**
 * Stores processed highlight with its calculated positions
 */
export interface PositionedHighlight extends PoemHighlight {
  // Contains highlight with additional position information
  positions: Array<{
    lineIndex: number;
    startIndex: number;
    endIndex: number;
    positionType: 'single' | 'start' | 'middle' | 'end';
  }>;
  isValid: boolean;
}

/**
 * Information about a highlight on a specific line
 */
export interface LineHighlightInfo {
  highlightId: string;
  startIndex: number;
  endIndex: number;
  positionType: HighlightPositionType;
  color: string;
  highlight: PoemHighlight;
  isActive?: boolean;
  stackIndex?: number; // Position in the stack of overlapping highlights (0 = bottom)
  stackTotal?: number; // Total number of highlights in this stack
}

/**
 * Information needed for legend display
 */
export interface LegendHighlightInfo {
  highlight: PoemHighlight;
  displayLabel: string;
  isMultiLine: boolean;
  isDisplayedInText: boolean;
  isActive: boolean;
}

/**
 * Segment of text for rendering with highlights
 */
export interface TextSegment {
  text: string;
  highlights: LineHighlightInfo[];
}

/**
 * Highlight position types
 */
export type HighlightPositionType = 'single' | 'start' | 'middle' | 'end';

/**
 * Centralized service for processing highlights.
 * This service handles all aspects of highlight processing including:
 * - Calculating highlight positions based on backend-validated indices
 * - Segmenting text with highlights
 * - Preparing highlight data for display components
 */
export class HighlightProcessingService {
  /**
   * Processes highlights for display in both the poem text and legend.
   * RELIES SOLELY on backend-validated indices for positioning.
   * @param poemText Array of poem lines
   * @param highlights Array of highlights to process (from backend)
   * @param activeHighlightId Optional ID of the active highlight
   * @returns Processed highlight data ready for display
   */
  processHighlightsForDisplay(
    poemText: string[],
    highlights: PoemHighlight[], // Assume these are validated by backend
    activeHighlightId?: string
  ): ProcessedHighlightData {
    logger.debug(`[processHighlightsForDisplay] Processing ${highlights.length} highlights for poem with ${poemText.length} lines (Using backend indices)`);

    // Log input highlights (briefly)
    if (highlights.length > 0) {
      logger.debug(`[processHighlightsForDisplay] Highlight samples (backend validated):`, highlights.slice(0, 3).map(h => ({
        id: h.id,
        text_preview: h.text?.substring(0, 20) + (h.text?.length > 20 ? '...' : ''),
        start_line: h.start_line_index,
        end_line: h.end_line_index,
        start_char: h.start_char_index,
        end_char: h.end_char_index
      })));
    }

    const allPositionedHighlights: PositionedHighlight[] = [];

    // --- Iterate and calculate positions directly from indices ---
    highlights.forEach(highlight => {
      try {
        // Calculate positions directly using the revised helper function
        // This function now handles the core logic and basic index validation
        const positionedHighlight = this.calculateHighlightPositionsFromIndices(highlight, poemText);
        allPositionedHighlights.push(positionedHighlight);

      } catch (error) {
        // Catch errors during direct positioning (e.g., fundamentally invalid indices from backend)
        logger.error(`[processHighlightsForDisplay] Error positioning highlight ${highlight.id} from indices:`, error);
        // Create an invalid highlight object
        allPositionedHighlights.push({
          ...highlight,
          note: (highlight.note || '') + ' [FRONTEND POSITIONING ERROR]',
          positions: [], // No valid positions
          isValid: false
        });
      }
    });
    // --- End direct position calculation ---

    logger.debug(`[processHighlightsForDisplay] Index-based positioning complete: ${allPositionedHighlights.length} highlights processed.`);

    // --- Organize highlights by line and calculate stacking info --- 
    const highlightsByLine: Record<number, LineHighlightInfo[]> = {};
    let totalLineHighlights = 0;

    for (let lineIndex = 0; lineIndex < poemText.length; lineIndex++) {
      const line = poemText[lineIndex];
      
      const relevantHighlights = allPositionedHighlights.filter(h => 
        h.isValid && // Only include highlights with valid positions
        h.positions.some(p => p.lineIndex === lineIndex)
      );

      if (relevantHighlights.length === 0) continue;

      const lineHighlightInfos: LineHighlightInfo[] = relevantHighlights.map(positionedHighlight => {
        const position = positionedHighlight.positions.find(p => p.lineIndex === lineIndex)!;
        // Add isActive based on comparison with activeHighlightId
        const isActive = positionedHighlight.id === activeHighlightId;
        return {
          highlightId: positionedHighlight.id,
          startIndex: position.startIndex,
          endIndex: position.endIndex,
          positionType: position.positionType,
          color: positionedHighlight.color,
          highlight: positionedHighlight, 
          isActive: isActive, // Set isActive flag here
          // stackIndex and stackTotal will be added below
        };
      });

      // Call segment calculation (which now includes logging)
      const segments = this.calculateLineSegments(line, lineHighlightInfos, lineIndex); // Pass lineIndex for logging
      
      const processedLineHighlightInfos: LineHighlightInfo[] = [];
      segments.forEach(segment => {
        segment.highlights.forEach(highlightInfo => {
          if (!processedLineHighlightInfos.some(existing => existing.highlightId === highlightInfo.highlightId)) {
            processedLineHighlightInfos.push(highlightInfo);
          }
        });
      });

      highlightsByLine[lineIndex] = processedLineHighlightInfos;
      totalLineHighlights += processedLineHighlightInfos.length;
    }
    
    // --- Create legend items --- 
    const legendItems = this.getHighlightsForLegend(allPositionedHighlights, activeHighlightId);

    // --- LOGGING: Check final highlightsByLine for specific lines --- START
    // Example: Log for lines 10 and 11 if they exist
    if (highlightsByLine[10]) {
      logger.debug(`[processHighlightsForDisplay] Final highlightsByLine[10]:`, JSON.stringify(highlightsByLine[10].map(h => ({id: h.highlightId, type: h.positionType, range: `${h.startIndex}-${h.endIndex}`} ) ) ) );
    }
    if (highlightsByLine[11]) {
       logger.debug(`[processHighlightsForDisplay] Final highlightsByLine[11]:`, JSON.stringify(highlightsByLine[11].map(h => ({id: h.highlightId, type: h.positionType, range: `${h.startIndex}-${h.endIndex}`} ) ) ) );
    }
    // --- LOGGING: Check final highlightsByLine for specific lines --- END

    logger.debug(`[processHighlightsForDisplay] Final processed data: ${Object.keys(highlightsByLine).length} lines with highlights, ${totalLineHighlights} total line highlights, ${legendItems.length} legend items`);

    // 6. Log highlight position summary for first few lines
    const firstFewLines = Object.keys(highlightsByLine).slice(0, 3).map(Number);
    if (firstFewLines.length > 0) {
      firstFewLines.forEach(lineIndex => {
        logger.debug(`[processHighlightsForDisplay] Line ${lineIndex} has ${highlightsByLine[lineIndex].length} highlights:`, 
          highlightsByLine[lineIndex].map(h => ({
            id: h.highlightId,
            positionType: h.positionType,
            range: `${h.startIndex}-${h.endIndex}`,
            isActive: h.isActive
          }))
        );
      });
    }
    
    return {
      highlightsByLine,
      allHighlights: allPositionedHighlights, // Return all, including invalid/unpositioned ones if needed
      legendItems
    };
  }

  /**
   * Calculates segments for a single line and adds stacking info
   * Added lineIndex for logging purposes
   */
  private calculateLineSegments(line: string, lineHighlightInfos: LineHighlightInfo[], lineIndex: number): TextSegment[] { 
    const events: Array<{ index: number; type: 'start' | 'end'; highlightInfo: LineHighlightInfo }> = [];
    lineHighlightInfos.forEach(info => {
      const startIndex = Math.min(line.length, Math.max(0, info.startIndex));
      const endIndex = Math.min(line.length, Math.max(0, info.endIndex));
      // LOGGING: Check start/end index before filtering
      // console.log(`[calculateLineSegments:${lineIndex}] Pre-filter Check: H_ID=${info.highlightId}, Orig S/E=(${info.startIndex}/${info.endIndex}), Clamped S/E=(${startIndex}/${endIndex})`);
      
      // --- MODIFIED: Allow zero-length segments --- 
      if (endIndex >= startIndex) { // Changed > to >= 
        events.push({ index: startIndex, type: 'start', highlightInfo: info });
        events.push({ index: endIndex, type: 'end', highlightInfo: info });
      } else {
        // LOGGING: Log when an event is skipped due to negative length (shouldn't happen with clamping, but kept for safety)
        logger.debug(`[calculateLineSegments:${lineIndex}] SKIPPED Event: H_ID=${info.highlightId}, Type=${info.positionType}, Clamped S/E=(${startIndex}/${endIndex}) because endIndex < startIndex`);
      }
    });

    events.sort((a, b) => a.index !== b.index ? a.index - b.index : (a.type === 'end' ? -1 : 1));

    // --- LOGGING: Show the generated events for this line --- START
    logger.debug(`[calculateLineSegments:${lineIndex}] Sorted Events:`, JSON.stringify(events.map(e => ({ idx: e.index, type: e.type, h_id: e.highlightInfo.highlightId, posType: e.highlightInfo.positionType }))));
    // --- LOGGING: Show the generated events for this line --- END

    const segments: TextSegment[] = [];
    let activeHighlights: LineHighlightInfo[] = [];
    let lastIndex = 0;

    for (const event of events) {
      if (event.index > lastIndex) {
        segments.push(this.createSegment(line.slice(lastIndex, event.index), activeHighlights));
      }

      if (event.type === 'start') {
        activeHighlights.push(event.highlightInfo);
      } else {
        activeHighlights = activeHighlights.filter(h => h.highlightId !== event.highlightInfo.highlightId);
      }
      
      // Ensure active highlights are sorted consistently, e.g., by ID
      activeHighlights.sort((a, b) => a.highlightId.localeCompare(b.highlightId));

      lastIndex = event.index;
    }

    if (lastIndex < line.length) {
      segments.push(this.createSegment(line.slice(lastIndex), activeHighlights));
    }

    return segments;
  }

  /**
   * Helper to create a segment and calculate stacking info
   */
  private createSegment(
    text: string, 
    activeHighlights: LineHighlightInfo[]
  ): TextSegment {
    const segmentHighlights = activeHighlights.map((highlightInfo, index) => ({
      ...highlightInfo,
      stackIndex: index, // Assign stack index based on sorted order
      stackTotal: activeHighlights.length
    }));
    // Return only properties defined in TextSegment interface
    return { text, highlights: segmentHighlights };
  }

  /**
   * REVISED: Calculates highlight positions based *only* on the provided start/end line/char indices.
   * Now includes basic index validation and returns the full PositionedHighlight.
   * @param highlight The PoemHighlight object from the backend (with validated indices).
   * @param poemText Array of poem lines (MUST be the original, unmodified lines).
   * @returns PositionedHighlight with calculated positions and validity status.
   */
  private calculateHighlightPositionsFromIndices(
    highlight: PoemHighlight,
    poemText: string[] // Expecting original, unmodified lines
  ): PositionedHighlight {
    const {
      start_line_index,
      end_line_index,
      start_char_index,
      end_char_index,
      id: highlightId // for error logging
    } = highlight;

    const positions: PositionedHighlight['positions'] = [];
    let isValid = true; // Assume valid initially

    // --- Comprehensive Index Validation ---
    // Check if indices are numbers and within poem line bounds
    if (typeof start_line_index !== 'number' || typeof end_line_index !== 'number' ||
        typeof start_char_index !== 'number' || typeof end_char_index !== 'number' ||
        start_line_index < 0 || start_line_index >= poemText.length ||
        end_line_index < 0 || end_line_index >= poemText.length ||
        end_line_index < start_line_index)
    {
      logger.error(`[calculateHighlightPositionsFromIndices] Invalid line indices for highlight ${highlightId}: sl=${start_line_index}, el=${end_line_index}, poemLines=${poemText.length}`);
      isValid = false;
    }
    // Check if character indices are within their respective line bounds *only if line indices are valid*
    // Important: Use poemText[lineIndex].length for comparison, NOT normalized length
    else if (start_char_index < 0 || start_char_index > poemText[start_line_index].length ||
             end_char_index < 0 || end_char_index > poemText[end_line_index].length ||
             (start_line_index === end_line_index && start_char_index >= end_char_index)) // Use >= for zero/negative length highlights
    {
      logger.error(`[calculateHighlightPositionsFromIndices] Invalid char indices for highlight ${highlightId}: sl=${start_line_index}(len:${poemText[start_line_index].length}), el=${end_line_index}(len:${poemText[end_line_index].length}), sc=${start_char_index}, ec=${end_char_index}`);
      isValid = false;
    }
    // --- End Index Validation ---

    // Calculate positions ONLY if indices passed validation
    if (isValid) {
      // --- Calculate line start offsets relative to the full poem string --- START
      const lineStartOffsets: number[] = [0];
      // IMPORTANT: Use poemText passed to this function (which should be the sanitized version without trailing spaces)
      for (let i = 0; i < poemText.length - 1; i++) {
          // Add 1 for the newline character
          lineStartOffsets.push(lineStartOffsets[i] + poemText[i].length + 1); 
      }
      // --- Calculate line start offsets relative to the full poem string --- END

      if (start_line_index === end_line_index) {
        // Single-line highlight
        positions.push({
            lineIndex: start_line_index,
            startIndex: start_char_index,
            endIndex: end_char_index,
            positionType: 'single'
        });
      } else {
        // Multi-line highlight
        const startLineLen = poemText[start_line_index].length;
        positions.push({
          lineIndex: start_line_index,
          startIndex: start_char_index,
          endIndex: startLineLen,
          positionType: 'start'
        });

        // Middle lines (if any)
        for (let i = start_line_index + 1; i < end_line_index; i++) {
          positions.push({
            lineIndex: i,
            startIndex: 0,
            endIndex: poemText[i].length,
            positionType: 'middle'
          });
        }

        // End line: Calculate relative index and clamp
        const endLineOffset = lineStartOffsets[end_line_index]; // Store for logging
        const relativeEndIndex = end_char_index - endLineOffset;
        const endLineLen = poemText[end_line_index].length; // Store for logging
        const clampedEndIndex = Math.max(0, Math.min(endLineLen, relativeEndIndex)); // Store for logging

        // --- LOGGING: Check end line index calculation --- START
        logger.debug(`[calculateHighlightPositionsFromIndices] End Line Calc: H_ID=${highlightId}, Line=${end_line_index}, DBEndChar=${end_char_index}, Offset=${endLineOffset}, RelEndIdx=${relativeEndIndex}, LineLen=${endLineLen}, ClampedEndIdx=${clampedEndIndex}`);
        // --- LOGGING: Check end line index calculation --- END

        positions.push({
          lineIndex: end_line_index,
          startIndex: 0,
          endIndex: clampedEndIndex, // Use the calculated clamped value
          positionType: 'end'
        });
      }
    } else {
        // If indices were invalid, log that we are skipping position calculation
        logger.warn(`[calculateHighlightPositionsFromIndices] Skipping position calculation for highlight ${highlightId} due to invalid indices.`);
    }

    // Return the full PositionedHighlight object
    return {
      ...highlight,
      positions, // Will be empty if isValid is false
      isValid // Reflects whether indices were valid
    };
  }

  /**
   * Gets highlight information for the legend display
   * @param highlights Array of processed highlights
   * @param activeHighlightId Optional ID of the active highlight
   * @returns Array of legend display information
   */
  getHighlightsForLegend(
    highlights: PositionedHighlight[],
    activeHighlightId?: string
  ): LegendHighlightInfo[] {
    const uniqueHighlights = new Map<string, PositionedHighlight>();
    highlights.forEach(h => {
      if (!uniqueHighlights.has(h.id)) {
        uniqueHighlights.set(h.id, h);
      }
    });

    return Array.from(uniqueHighlights.values()).map(highlight => ({
      highlight,
      displayLabel: this.getDisplayLabel(highlight),
      isMultiLine: highlight.start_line_index !== highlight.end_line_index,
      isDisplayedInText: highlight.isValid && highlight.positions.length > 0, // Check validity flag
      isActive: highlight.id === activeHighlightId
    }));
  }

  /**
   * Gets a display label for a highlight
   * @param highlight The highlight to get a label for
   * @returns The display label
   */
  getDisplayLabel(highlight: PoemHighlight): string {
    // Use the new utility function that implements robust label generation
    return getDisplayLabelFromUtils(highlight);
  }

  /**
   * Determines the type of a highlight's position on a line
   * @param highlight The highlight to check
   * @param lineIndex The line index to check
   * @returns The position type
   */
  determineHighlightPositionType(
    highlight: PoemHighlight, 
    lineIndex: number
  ): HighlightPositionType {
    // Single line highlight
    if (highlight.start_line_index === highlight.end_line_index) {
      return 'single';
    }
    
    // First line of multi-line highlight
    if (lineIndex === highlight.start_line_index) {
      return 'start';
    }
    
    // Last line of multi-line highlight
    if (lineIndex === highlight.end_line_index) {
      return 'end';
    }
    
    // Middle line of multi-line highlight
    return 'middle';
  }

  /**
   * Prioritizes highlights when overlapping
   * @param highlights Array of highlights to prioritize
   * @returns Prioritized highlights array with assigned z-index and stacking info
   */
  prioritizeHighlights(
    highlights: LineHighlightInfo[]
  ): LineHighlightInfo[] {
    if (!highlights || highlights.length <= 1) {
      return highlights;
    }

    // Create a copy to avoid modifying the original array
    const prioritizedHighlights: LineHighlightInfo[] = highlights.map(highlight => ({
      ...highlight,
      stackIndex: 0, // Default stack index
      stackTotal: 1, // By default each highlight is alone
    }));

    // Sort highlights by preset criteria to determine display priority
    // 1. Active highlights (if any) get higher priority
    // 2. Multi-line highlights get higher priority than single-line
    // 3. Shorter highlights (by text length) get higher priority than longer ones
    // 4. Finally, sort by color to ensure consistent ordering
    prioritizedHighlights.sort((a, b) => {
      // Active highlight gets priority
      const aActive = a.isActive === true;
      const bActive = b.isActive === true;
      if (aActive !== bActive) return bActive ? 1 : -1;
      
      // Multi-line highlights get priority over single-line
      const aIsMultiLine = a.highlight.start_line_index !== a.highlight.end_line_index;
      const bIsMultiLine = b.highlight.start_line_index !== b.highlight.end_line_index;
      if (aIsMultiLine !== bIsMultiLine) return aIsMultiLine ? -1 : 1;
      
      // Shorter highlights get priority for better visibility
      const aLength = a.endIndex - a.startIndex;
      const bLength = b.endIndex - b.startIndex;
      if (aLength !== bLength) return aLength - bLength;
      
      // Finally sort by color for consistent ordering
      return (a.color || '').localeCompare(b.color || '');
    });
    
    // Calculate stack indices based on overlap groups
    // For each highlight, identify all other highlights that overlap with it
    // and assign sequential stack indices to them
    const overlapGroups: LineHighlightInfo[][] = [];
    
    for (let i = 0; i < prioritizedHighlights.length; i++) {
      const currentHighlight = prioritizedHighlights[i];
      
      // Check if this highlight overlaps with any existing group
      let foundGroup = false;
      for (const group of overlapGroups) {
        // Check if highlight overlaps with any highlight in this group
        if (group.some(member => this.highlightsOverlap(currentHighlight, member))) {
          group.push(currentHighlight);
          foundGroup = true;
          break;
        }
      }
      
      // If no overlapping group was found, create a new one
      if (!foundGroup) {
        overlapGroups.push([currentHighlight]);
      }
    }
    
    // Now assign stack indices to each highlight based on its group
    overlapGroups.forEach(group => {
      // No need to assign stack indices for single-highlight groups
      if (group.length <= 1) return;
      
      // Sort by our priority rules
      group.sort((a, b) => {
        const aIndex = prioritizedHighlights.indexOf(a);
        const bIndex = prioritizedHighlights.indexOf(b);
        return aIndex - bIndex;
      });
      
      // Assign stack indices and total
      group.forEach((highlight, index) => {
        const origHighlight = prioritizedHighlights.find(h => h.highlightId === highlight.highlightId);
        if (origHighlight) {
          origHighlight.stackIndex = index;
          origHighlight.stackTotal = group.length;
        }
      });
    });
    
    return prioritizedHighlights;
  }
  
  /**
   * Checks if two highlights overlap
   * @param a First highlight
   * @param b Second highlight
   * @returns true if the highlights overlap
   */
  private highlightsOverlap(a: LineHighlightInfo, b: LineHighlightInfo): boolean {
    // Simple overlap check: max(start1, start2) < min(end1, end2)
    return Math.max(a.startIndex, b.startIndex) < Math.min(a.endIndex, b.endIndex);
  }
}

// Export a singleton instance for use throughout the application
export const highlightProcessor = new HighlightProcessingService(); 